import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, X, Flame, Save, FileText, AlertTriangle, Check, Upload, Download, ClipboardPaste, Pencil, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import TranchesEligibilite from '../components/TranchesEligibilite';
import ModalBasculeSIRET from '../components/ModalBasculeSIRET';
import IcpeRubriqueSelector from '../components/IcpeRubriqueSelector';
import DoubleUsageBlock from '../components/DoubleUsageBlock';
import { StepValidationTICGN } from '../components/ticgn/StepValidationTICGN';
import LignesEnergiesEditor from '../components/ticgn/LignesEnergiesEditor';
import * as XLSX from 'xlsx';
import {
  listBrouillons as apiListBrouillons,
  saveBrouillon as apiSaveBrouillon,
  deleteBrouillon as apiDeleteBrouillon,
  getBrouillon as apiGetBrouillon,
  migrateLocalStorageBrouillons,
} from '../utils/brouillonsApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// === Composant autocomplétion Code NAF TICGN ===
const CodeNAFAutocomplete = ({ value, onChange }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/eligibility-rules-ticgn?search=${encodeURIComponent(query)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(Array.isArray(data) ? data : []);
        }
      } catch (e) { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-[#687175] mb-1">Code NAF</label>
      <input type="text" value={query} placeholder="Ex: C1071"
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setShowSuggestions(true); }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
        data-testid="code-naf-input"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#BFC6CC] rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button key={i} type="button"
              onMouseDown={() => { onChange(s.naf_code_label || s.naf_code); setQuery(s.naf_code_label || s.naf_code); setShowSuggestions(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#FFF7ED] border-b last:border-b-0">
              <span className="font-mono font-bold text-[#F97316]">{s.naf_code_label || s.naf_code}</span>
              <span className="ml-2 text-[#687175]">{s.libelle || s.description}</span>
              {s.eligibility_type && s.eligibility_type !== 'NON_CONCERNE' && (
                <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">Eligible</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// === AGENCES / COMMERCIAUX (fallback) — chargés en runtime depuis l'API ===
const _AGENCES_FALLBACK = [
  { id: 'siege', nom: 'Siege' }, { id: 'cambrai', nom: 'Cambrai' }, { id: 'yonne', nom: 'Yonne' },
  { id: 'belfort', nom: 'Belfort' }, { id: 'bouches', nom: 'Bouches-du-Rhone' }, { id: 'cote_or', nom: "Cote-d'Or" },
  { id: 'pyrenees', nom: 'Pyrenees' }, { id: 'somme', nom: 'Somme' }, { id: 'haute_savoie', nom: 'Haute-Savoie' },
  { id: 'vendee', nom: 'Vendee' }, { id: 'calvados', nom: 'Calvados' }, { id: 'cote_opale', nom: "Cote d'Opale" },
  { id: 'dunkerque', nom: 'Dunkerque' }
];
const _COMMERCIAUX_FALLBACK = [];

// === COMPOSANT PRINCIPAL ===
const CreateRapportTICGN = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultats, setResultats] = useState(null);
  const [rapportType, setRapportType] = useState('mono');

  // Formulaire
  const [formData, setFormData] = useState({
    siren: '', nom_entreprise: '', valeur_ajoutee_siren: '',
    debut_periode: '', fin_periode: '', agence_id: '', commercial_id: '',
    commentaire_global: '',
    pces: [], sites: [],
    // Coche entreprise (mono) — Soumis aux quotas CO2 (motif tarif réduit dispo, soumis ratio)
    soumis_quotas_co2: false,
    // Valeur de Production (fallback tarif réduit si non éligible classique)
    valeur_production: { ca: '', export: '', import: '' },
    factures_htva: { elec: '', gaz: '', autres_energies: '' },
    lignes_energies: [],
    // VP par SIRET (variante multi-sites quand SIREN ne passe pas)
    vp_par_siret: {},
  });

  // PCE courant (mono)
  const [currentPCE, setCurrentPCE] = useState({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } });
  const [editingPCEIndex, setEditingPCEIndex] = useState(null);

  // Multi-site
  const [currentSite, setCurrentSite] = useState({ siret: '', nom_site: '', valeur_ajoutee: '', taux_reduit_deja_applique: false, commentaire: '', pces: [] });
  const [currentPCEMulti, setCurrentPCEMulti] = useState({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } });
  const [editingSiteIndex, setEditingSiteIndex] = useState(null);
  const [editingPCEMultiIndex, setEditingPCEMultiIndex] = useState(null);

  // Consommations
  const [consommationsParPCE, setConsommationsParPCE] = useState({});

  // Catalogue tarifs fiscaux par énergie (Charbon, Fioul, Gaz, Élec, …) —
  // utilisé pour le ratio multi-énergies + le selecteur "Énergie" sur chaque PDL/PCE.
  const [energiesCatalog, setEnergiesCatalog] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/tarifs-fiscaux-energies`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setEnergiesCatalog(Array.isArray(data.items) ? data.items : []);
        }
      } catch (_) { /* silencieux */ }
    })();
  }, []);

  // Brouillons (stockés côté serveur via /api/brouillons)
  const [brouillons, setBrouillons] = useState([]);
  const [currentBrouillonId, setCurrentBrouillonId] = useState(null);

  // Mode "VP activée" — bouton manuel dans étape 2 (raccourci) OU bouton dans étape 6
  // quand non éligible par voie classique. Quand true, la step 7 (VP) s'affiche et
  // doit être traitée avant la génération du rapport.
  const [vpActivated, setVpActivated] = useState(false);
  const [vpCameFromQuickJump, setVpCameFromQuickJump] = useState(false);

  // Bascule SIRET
  const [showModalBasculeSIRET, setShowModalBasculeSIRET] = useState(false);
  const [eligibiliteData, setEligibiliteData] = useState(null);
  const [resultatsParSiret, setResultatsParSiret] = useState(null);
  const [isCalculatingSiret, setIsCalculatingSiret] = useState(false);
  const [modeCalcul, setModeCalcul] = useState('siren');
  // Cache des types d'éligibilité NAF (clé: code_naf, valeur: eligibility_type ou null)
  const [nafEligMap, setNafEligMap] = useState({});

  // Référentiel agences/commerciaux (chargé depuis l'API, fallback constants)
  const [agences, setAgences] = useState(_AGENCES_FALLBACK);
  const [commerciaux, setCommerciaux] = useState(_COMMERCIAUX_FALLBACK);

  const commerciauxFiltres = formData.agence_id ? commerciaux.filter(c => c.agence_id === formData.agence_id) : commerciaux;

  // === Charger référentiel agences/commerciaux ===
  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agences`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commerciaux`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ]).then(([a, c]) => {
      if (Array.isArray(a) && a.length) setAgences(a);
      if (Array.isArray(c) && c.length) setCommerciaux(c);
    }).catch(() => {});
  }, []);

  // === Charger brouillons (depuis le serveur) ===
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Migration silencieuse depuis localStorage (1 fois max par utilisateur)
        await migrateLocalStorageBrouillons('ticgn', (b) => ({
          nom_entreprise: b.nom_entreprise || b.formData?.nom_entreprise || '',
          siren: b.siren || b.formData?.siren || '',
          step: 2,
          payload: {
            formData: b.formData,
            rapportType: b.rapportType,
            consommationsParPCE: b.consommationsParPCE,
          },
        }));
        const saved = await apiListBrouillons('ticgn');
        if (!cancelled) setBrouillons(Array.isArray(saved) ? saved : []);
      } catch {
        if (!cancelled) setBrouillons([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // === Reprise via URL ?brouillon=<id> ===
  useEffect(() => {
    const brouillonParam = searchParams.get('brouillon');
    if (!brouillonParam) return;
    (async () => {
      try {
        const b = await apiGetBrouillon(brouillonParam);
        if (!b) {
          alert('Brouillon introuvable');
          return;
        }
        const p = b.payload || {};
        if (p.formData) setFormData(p.formData);
        setRapportType(p.rapportType || 'mono');
        setConsommationsParPCE(p.consommationsParPCE || {});
        setCurrentBrouillonId(b.id);
        setStep(b.step || 2);
      } catch (e) {
        console.error('Erreur reprise brouillon:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // === Sauvegarder brouillon ===
  const saveBrouillon = useCallback(async () => {
    if (!formData.siren && !formData.nom_entreprise) return false;
    try {
      const saved = await apiSaveBrouillon({
        id: currentBrouillonId || undefined,
        type: 'ticgn',
        nom_entreprise: formData.nom_entreprise || '',
        siren: formData.siren || '',
        step,
        payload: { formData, rapportType, consommationsParPCE },
      });
      setCurrentBrouillonId(saved.id);
      // Rafraîchir la liste
      const list = await apiListBrouillons('ticgn');
      setBrouillons(Array.isArray(list) ? list : []);
      return true;
    } catch (e) {
      console.error('Erreur sauvegarde brouillon:', e);
      return false;
    }
  }, [formData, rapportType, consommationsParPCE, currentBrouillonId, step]);

  // === Sauvegarde manuelle (bouton) avec feedback utilisateur ===
  const handleManualSaveBrouillon = useCallback(async () => {
    const ok = await saveBrouillon();
    if (!ok) {
      alert("Renseignez au minimum un SIREN ou un nom d'entreprise avant d'enregistrer un brouillon.");
      return;
    }
    alert(`✓ Brouillon enregistré\n\n${formData.nom_entreprise || formData.siren}\nÉtape ${step} - ${rapportType === 'mono' ? 'Mono-site' : 'Multi-sites'}\n\nVous pourrez le reprendre depuis l'étape 1.`);
  }, [saveBrouillon, formData.nom_entreprise, formData.siren, step, rapportType]);

  const loadBrouillon = (b) => {
    const p = b.payload || {};
    setFormData(p.formData || formData);
    setRapportType(p.rapportType || 'mono');
    setConsommationsParPCE(p.consommationsParPCE || {});
    setCurrentBrouillonId(b.id);
    setStep(2);
  };

  // === Fetch NAF eligibility when reaching Step 6 ===
  useEffect(() => {
    if (step !== 6) return;
    const token = localStorage.getItem('token');
    const pcesAll = rapportType === 'multi'
      ? formData.sites.flatMap(s => (s.pces || []))
      : formData.pces;
    const codes = [...new Set(pcesAll.map(p => (p.code_naf || '').trim().toUpperCase()).filter(Boolean))];
    const toFetch = codes.filter(c => !(c in nafEligMap));
    if (toFetch.length === 0) return;
    (async () => {
      const updates = {};
      await Promise.all(toFetch.map(async (code) => {
        try {
          const res = await fetch(`${API_URL}/api/ticgn/naf/${encodeURIComponent(code)}/eligibilite`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            updates[code] = data.eligibility_type || null;
          } else {
            updates[code] = null;
          }
        } catch { updates[code] = null; }
      }));
      setNafEligMap(prev => ({ ...prev, ...updates }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rapportType, formData.pces, formData.sites]);

  const deleteBrouillon = async (id) => {
    try {
      await apiDeleteBrouillon(id);
      setBrouillons(prev => prev.filter(b => b.id !== id));
      if (currentBrouillonId === id) setCurrentBrouillonId(null);
    } catch (e) {
      console.error('Erreur suppression brouillon:', e);
      alert('Erreur lors de la suppression du brouillon.');
    }
  };

  // === Mode edition ===
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/ticgn/rapports/${editId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const r = await res.json();
            setRapportType(r.type_rapport || 'mono');
            const agenceId = commerciaux.find(c => c.id === String(r.commercial_id))?.agence_id || '';
            setFormData({
              siren: r.siren || '', nom_entreprise: r.nom_entreprise || '',
              valeur_ajoutee_siren: r.valeur_ajoutee || '',
              commentaire_global: r.commentaire_global || '',
              debut_periode: r.debut_periode ? r.debut_periode.substring(0, 10) : '',
              fin_periode: r.fin_periode ? r.fin_periode.substring(0, 10) : '',
              agence_id: agenceId, commercial_id: r.commercial_id ? String(r.commercial_id) : '',
              pces: (r.pces_data || []).map(p => ({
                pce: p.pce, code_naf: p.code_naf,
                tranches_eligibilite: p.tranches_detail?.filter(t => t.motif !== 'NORMAL').map(t => ({ part: t.part, motif: t.motif, description: t.description || '' })) || []
              })),
              sites: r.sites || [],
              soumis_quotas_co2: !!r.soumis_quotas_co2,
              valeur_production: {
                ca: r.valeur_production?.ca ?? '',
                export: r.valeur_production?.export ?? '',
                import: r.valeur_production?.import ?? '',
              },
              lignes_energies: (r.valeur_production?.lignes_energies || []).map(l => ({
                energie_code: l.energie_code || '',
                libelle: l.libelle || '',
                consommation_mwh: l.consommation_mwh ?? '',
                tarif_fiscal: l.tarif_fiscal ?? '',
              })),
              vp_par_siret: Object.fromEntries(
                Object.entries(r.vp_par_siret || {}).map(([k, v]) => [k, {
                  ca: v.ca ?? '', export: v.export ?? '', import: v.import ?? '',
                  lignes_energies: (v.lignes_energies || []).map(l => ({
                    energie_code: l.energie_code || '',
                    libelle: l.libelle || '',
                    consommation_mwh: l.consommation_mwh ?? '',
                    tarif_fiscal: l.tarif_fiscal ?? '',
                  })),
                }])
              ),
            });
            setConsommationsParPCE(r.consommations_par_pce || {});
            // Active automatiquement la step VP si le rapport a déjà des données VP
            const hasVpData = (r.valeur_production?.lignes_energies || []).length > 0
              || Object.values(r.vp_par_siret || {}).some(v => (v.lignes_energies || []).length > 0);
            if (hasVpData) setVpActivated(true);
            setStep(2);
          }
        } catch (e) { console.error('Erreur chargement rapport:', e); }
      })();
    }
  }, [searchParams]);

  // === Mois de la periode ===
  const moisPeriode = useMemo(() => {
    if (!formData.debut_periode || !formData.fin_periode) return [];
    const mois = [];
    const debut = new Date(formData.debut_periode);
    const fin = new Date(formData.fin_periode);
    const current = new Date(debut.getFullYear(), debut.getMonth(), 1);
    const finMonth = new Date(fin.getFullYear(), fin.getMonth(), 1);
    while (current <= finMonth) {
      const key = `${current.getFullYear()}-${current.getMonth() + 1}`;
      const label = `${String(current.getMonth() + 1).padStart(2, '0')}/${current.getFullYear()}`;
      mois.push({ key, label });
      current.setMonth(current.getMonth() + 1);
    }
    return mois;
  }, [formData.debut_periode, formData.fin_periode]);

  // Tous les PCEs
  const allPCEs = useMemo(() => {
    if (rapportType === 'mono') return formData.pces;
    return formData.sites.flatMap(s => (s.pces || []).map(p => ({ ...p, siret: s.siret, nom_site: s.nom_site })));
  }, [formData, rapportType]);

  // === PCE actions ===
  const addPCE = () => {
    if (!currentPCE.pce || !currentPCE.code_naf) return;
    if (editingPCEIndex !== null) {
      const updated = [...formData.pces];
      updated[editingPCEIndex] = { ...currentPCE };
      setFormData({ ...formData, pces: updated });
      setEditingPCEIndex(null);
    } else {
      setFormData({ ...formData, pces: [...formData.pces, { ...currentPCE }] });
    }
    setCurrentPCE({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } });
  };

  const addPCEToSite = () => {
    if (!currentPCEMulti.pce || !currentPCEMulti.code_naf) return;
    if (editingPCEMultiIndex !== null) {
      const updated = [...currentSite.pces];
      updated[editingPCEMultiIndex] = { ...currentPCEMulti };
      setCurrentSite({ ...currentSite, pces: updated });
      setEditingPCEMultiIndex(null);
    } else {
      setCurrentSite({ ...currentSite, pces: [...currentSite.pces, { ...currentPCEMulti }] });
    }
    setCurrentPCEMulti({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } });
  };

  const editPCEInSite = (idx) => {
    setCurrentPCEMulti({ ...currentSite.pces[idx] });
    setEditingPCEMultiIndex(idx);
  };

  const removePCEFromSite = (idx) => {
    setCurrentSite({ ...currentSite, pces: currentSite.pces.filter((_, i) => i !== idx) });
    if (editingPCEMultiIndex === idx) {
      setCurrentPCEMulti({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } });
      setEditingPCEMultiIndex(null);
    }
  };

  const addSite = () => {
    if (!currentSite.siret || currentSite.pces.length === 0) return;
    if (editingSiteIndex !== null) {
      const updated = [...formData.sites];
      updated[editingSiteIndex] = { ...currentSite };
      setFormData({ ...formData, sites: updated });
      setEditingSiteIndex(null);
    } else {
      setFormData({ ...formData, sites: [...formData.sites, { ...currentSite }] });
    }
    setCurrentSite({ siret: '', nom_site: '', valeur_ajoutee: '', taux_reduit_deja_applique: false, commentaire: '', pces: [] });
  };

  // === Copier-coller consommations ===
  const handlePasteConsommations = (pceNumber, event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const values = [];
    pastedData.split('\n').forEach(line => {
      line.trim().split('\t').forEach(p => {
        const val = parseFloat(p.trim().replace(',', '.').replace(/\s/g, ''));
        if (!isNaN(val)) values.push(val);
      });
    });
    if (values.length === 0) { alert('Aucune valeur numerique trouvee'); return; }
    const newConso = { ...consommationsParPCE };
    if (!newConso[pceNumber]) newConso[pceNumber] = {};
    values.forEach((val, i) => { if (i < moisPeriode.length) newConso[pceNumber][moisPeriode[i].key] = val; });
    setConsommationsParPCE(newConso);
    alert(`${values.length} valeur(s) collee(s) pour le PCE ${pceNumber}`);
  };

  // === Import Excel en masse ===
  const handleImportConsommationsExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const range = XLSX.utils.decode_range(ws['!ref']);
        const grouped = {};
        let detected = new Set();
        for (let row = range.s.r + 1; row <= range.e.r; row++) {
          const pceCell = ws[XLSX.utils.encode_cell({ r: row, c: 4 })];
          const dateCell = ws[XLSX.utils.encode_cell({ r: row, c: 2 })];
          const consoCell = ws[XLSX.utils.encode_cell({ r: row, c: 3 })];
          if (!pceCell || !dateCell || !consoCell) continue;
          const pce = String(pceCell.v), dateValue = dateCell.v, conso = parseFloat(consoCell.v);
          if (!pce || !dateValue || isNaN(conso)) continue;
          let mk;
          if (typeof dateValue === 'number') { const d = new Date((dateValue - 25569) * 86400 * 1000); mk = `${d.getFullYear()}-${d.getMonth() + 1}`; }
          else if (typeof dateValue === 'string' && dateValue.includes('-')) { const p = dateValue.split('-'); mk = `${p[0]}-${parseInt(p[1])}`; }
          if (!mk) continue;
          detected.add(pce);
          if (!grouped[pce]) grouped[pce] = {};
          grouped[pce][mk] = (grouped[pce][mk] || 0) + conso;
        }
        setConsommationsParPCE(grouped);
        alert(`Import reussi !\n${detected.size} PCE detecte(s)`);
      } catch (err) { alert('Erreur import'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // === Import multi-sites Excel ===
  const handleImportSitesExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);
        const sitesMap = {};
        rows.forEach(row => {
          const siret = String(row['SIRET'] || row['siret'] || '').trim();
          const nomSite = row['Nom Site'] || row['nom_site'] || '';
          const va = parseFloat(row['VA'] || row['valeur_ajoutee'] || 0);
          const pce = String(row['PCE'] || row['pce'] || '').trim();
          const naf = String(row['Code NAF'] || row['code_naf'] || '').trim();
          if (!siret || !pce) return;
          if (!sitesMap[siret]) sitesMap[siret] = { siret, nom_site: nomSite, valeur_ajoutee: va, taux_reduit_deja_applique: false, pces: [] };
          sitesMap[siret].pces.push({ pce, code_naf: naf, tranches_eligibilite: [] });
        });
        const sites = Object.values(sitesMap);
        setFormData({ ...formData, sites });
        alert(`Import reussi : ${sites.length} site(s), ${sites.reduce((s, si) => s + si.pces.length, 0)} PCE(s)`);
      } catch (err) { alert('Erreur import sites'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // === Download template ===
  const downloadConsoTemplate = () => {
    const rows = [['Mois', 'Annee', 'Date', 'Consommation (kWh)', 'N PCE']];
    allPCEs.forEach(pce => {
      moisPeriode.forEach(m => { const p = m.key.split('-'); rows.push([parseInt(p[1]), parseInt(p[0]), `${p[0]}-${String(p[1]).padStart(2, '0')}-01`, '', pce.pce]); });
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Consommations');
    XLSX.writeFile(wb, `template_conso_ticgn_${formData.siren || 'rapport'}.xlsx`);
  };

  const downloadSitesTemplate = () => {
    const rows = [['SIRET', 'Nom Site', 'VA', 'PCE', 'Code NAF']];
    rows.push(['12345678901234', 'Site exemple', '100000', 'GI000000', 'C2014']);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sites');
    XLSX.writeFile(wb, `template_sites_ticgn_${formData.siren || 'rapport'}.xlsx`);
  };

  // === Soumission avec vérification SIREN → SIRET ===
  const handleGenerate = async () => {
    saveBrouillon();

    // Pour les multi-sites en mode SIREN, vérifier d'abord l'éligibilité
    if (rapportType === 'multi' && modeCalcul === 'siren' && formData.sites.length > 1) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/ticgn/rapports/verify-eligibilite`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            valeur_ajoutee_siren: parseFloat(formData.valeur_ajoutee_siren) || 0,
            sites: formData.sites,
            consommations_par_pce: consommationsParPCE
          })
        });
        if (res.ok) {
          const eligData = await res.json();
          if (eligData.peut_basculer_siret) {
            setEligibiliteData({
              ...eligData,
              valeur_ajoutee_siren: parseFloat(formData.valeur_ajoutee_siren),
              nb_siret: eligData.siret_groups.length
            });
            setResultatsParSiret(null);
            setShowModalBasculeSIRET(true);
            return;
          }
        }
      } catch (e) {
        console.error('Erreur vérification éligibilité:', e);
      }
    }

    await createRapportFinal();
  };

  // Créer le rapport final
  const createRapportFinal = async (modeOverride = null) => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const editId = searchParams.get('edit');
      const url = editId ? `${API_URL}/api/ticgn/rapports/${editId}` : `${API_URL}/api/ticgn/rapports`;
      const method = editId ? 'PUT' : 'POST';
      const payload = {
        siren: formData.siren, nom_entreprise: formData.nom_entreprise,
        valeur_ajoutee_siren: parseFloat(formData.valeur_ajoutee_siren) || 0,
        commentaire_global: (formData.commentaire_global || '').trim(),
        debut_periode: formData.debut_periode, fin_periode: formData.fin_periode,
        commercial_id: formData.commercial_id || null,
        type_rapport: rapportType, mode_calcul: modeOverride || modeCalcul,
        pces: rapportType === 'mono' ? formData.pces : [],
        sites: rapportType === 'multi' ? formData.sites : [],
        consommations_par_pce: consommationsParPCE,
        soumis_quotas_co2: !!formData.soumis_quotas_co2,
        valeur_production: {
          ca: parseFloat(formData.valeur_production?.ca) || 0,
          export: parseFloat(formData.valeur_production?.export) || 0,
          import: parseFloat(formData.valeur_production?.import) || 0,
        },
        lignes_energies: (formData.lignes_energies || [])
          .filter(l => l && l.energie_code && (parseFloat(l.consommation_mwh) || 0) > 0)
          .map(l => ({
            energie_code: l.energie_code,
            libelle: l.libelle || l.energie_code,
            consommation_mwh: parseFloat(l.consommation_mwh) || 0,
            tarif_fiscal: parseFloat(l.tarif_fiscal) || 0,
          })),
        vp_par_siret: Object.fromEntries(
          Object.entries(formData.vp_par_siret || {}).map(([k, v]) => [k, {
            ...v,
            lignes_energies: (v?.lignes_energies || [])
              .filter(l => l && l.energie_code && (parseFloat(l.consommation_mwh) || 0) > 0)
              .map(l => ({
                energie_code: l.energie_code,
                libelle: l.libelle || l.energie_code,
                consommation_mwh: parseFloat(l.consommation_mwh) || 0,
                tarif_fiscal: parseFloat(l.tarif_fiscal) || 0,
              })),
          }])
        ),
      };
      const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      // Lire le body une seule fois sous forme de texte, puis tenter le parse JSON
      const rawBody = await res.text();
      let body = null;
      try { body = rawBody ? JSON.parse(rawBody) : null; } catch { body = null; }
      if (res.ok) {
        setResultats(body);
        setStep(RESULT_STEP);
        // Nettoyage : supprimer le brouillon associé une fois le rapport généré
        if (currentBrouillonId) {
          try { await apiDeleteBrouillon(currentBrouillonId); } catch {}
          setCurrentBrouillonId(null);
        }
      } else {
        // Gère 500 plain-text, 422 JSON FastAPI, ou body absent
        let msg;
        if (body && typeof body === 'object') {
          if (Array.isArray(body.detail)) {
            // 422 FastAPI : agrège les erreurs de validation
            msg = body.detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(' · ');
          } else {
            msg = body.detail || body.message || JSON.stringify(body);
          }
        } else {
          msg = rawBody?.slice(0, 200) || `HTTP ${res.status}`;
        }
        setError(`Erreur lors de la creation du rapport (HTTP ${res.status}) : ${msg}`);
      }
    } catch (e) { setError('Erreur reseau : ' + e.message); }
    finally { setLoading(false); setShowModalBasculeSIRET(false); }
  };

  // Calculer éligibilité par SIRET
  const handleCalculerParSiret = async (mode, vacManuelles) => {
    setIsCalculatingSiret(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ticgn/rapports/calcul-eligibilite-siret`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          valeur_ajoutee_siren: parseFloat(formData.valeur_ajoutee_siren) || 0,
          vac_manuelles: vacManuelles || {},
          sites: formData.sites,
          consommations_par_pce: consommationsParPCE
        })
      });
      if (res.ok) {
        setResultatsParSiret(await res.json());
      }
    } catch (e) { alert('Erreur calcul par SIRET'); }
    finally { setIsCalculatingSiret(false); }
  };

  // Confirmer la bascule SIRET et créer le rapport
  const handleConfirmBasculeSIRET = async () => {
    setModeCalcul('siret');
    // Ajouter la VA attribuée à chaque site dans formData
    if (resultatsParSiret) {
      const sitesUpdated = formData.sites.map(site => {
        const res = resultatsParSiret.resultats_siret?.find(r => r.siret === site.siret);
        if (res) {
          return { ...site, valeur_ajoutee: res.vac_attribuee, pces: site.pces.map(p => ({ ...p, valeur_ajoutee_site: res.vac_attribuee })) };
        }
        return site;
      });
      setFormData({ ...formData, sites: sitesUpdated });
    }
    await createRapportFinal('siret');
  };

  const stepLabels = ['Type', 'Entreprise', 'PCE / Sites', 'Consommations', 'Recapitulatif', 'Validation', 'Valeur de Production', 'Resultats'];
  // Indices internes (1-based) — la step 7 (VP) est masquée tant que vpActivated=false.
  const VP_STEP = 7;
  const RESULT_STEP = 8;

  const canNext = () => {
    switch (step) {
      case 1: return true;
      case 2: return formData.siren && formData.nom_entreprise && formData.debut_periode && formData.fin_periode && formData.agence_id && formData.commercial_id;
      case 3: return allPCEs.length > 0;
      case 4: return Object.keys(consommationsParPCE).length > 0;
      case 5: return true;
      case 6: return vpActivated;  // Si VP activée → Suivant mène à step 7
      case 7: return true;
      default: return false;
    }
  };

  const formatEur = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-[#687175] hover:text-[#191D1E]"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-[#191D1E]">
              {searchParams.get('edit') ? 'Modifier le Rapport TICGN' : 'Nouveau Rapport TICGN'}
            </h1>
            <p className="text-sm text-[#687175]">Taxe Interieure de Consommation sur le Gaz Naturel</p>
          </div>
        </div>
        <Flame className="w-8 h-8 text-[#F97316]" />
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-1 mb-8 bg-[#F5F6F9] rounded-full p-1.5 flex-wrap">
        {(() => {
          // Numérotation visible : compte uniquement les étapes affichées (VP masquée tant que !vpActivated)
          let visibleIdx = 0;
          return stepLabels.map((label, i) => {
            const stepNum = i + 1;
            if (stepNum === VP_STEP && !vpActivated) return null;
            visibleIdx += 1;
            const displayNum = visibleIdx;
            return (
              <button key={i} disabled={stepNum > step}
                onClick={() => { if (stepNum <= step) setStep(stepNum); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  step === stepNum ? 'bg-white text-[#F97316] shadow-sm' : step > stepNum ? 'text-[#10B981]' : 'text-[#687175]'
                }`}>
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] ${
                  step === stepNum ? 'bg-[#F97316] text-white' : step > stepNum ? 'bg-[#10B981] text-white' : 'bg-[#BFC6CC] text-white'
                }`}>{step > stepNum ? <Check className="w-3 h-3" /> : (stepNum === VP_STEP ? '💜' : displayNum)}</span>
                {label}
              </button>
            );
          });
        })()}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}

      {/* === STEP 1: Type === */}
      {step === 1 && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-type">
          <h2 className="text-lg font-semibold text-[#191D1E] mb-4">Type de rapport</h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[{ id: 'mono', label: 'Mono-Site', desc: 'Un seul site, un ou plusieurs PCE' },
              { id: 'multi', label: 'Multi-Sites', desc: 'Plusieurs sites avec leurs PCE' }
            ].map(t => (
              <button key={t.id} onClick={() => setRapportType(t.id)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${rapportType === t.id ? 'border-[#F97316] bg-[#FFF7ED]' : 'border-[#BFC6CC] hover:border-[#F97316]'}`}
                data-testid={`type-${t.id}`}>
                <div className="font-semibold text-[#191D1E]">{t.label}</div>
                <div className="text-sm text-[#687175] mt-1">{t.desc}</div>
              </button>
            ))}
          </div>
          {/* Brouillons */}
          {brouillons.length > 0 && (
            <div className="border-t border-[#BFC6CC] pt-4">
              <h3 className="text-sm font-medium text-[#687175] mb-2">Brouillons sauvegardes</h3>
              {brouillons.map(b => (
                <div key={b.id} className="flex items-center justify-between p-2 bg-[#F5F6F9] rounded mb-1">
                  <div className="text-sm"><span className="font-medium">{b.nom_entreprise || b.siren}</span> <span className="text-[#687175]">({new Date(b.updated_at || b.date || b.created_at).toLocaleDateString('fr-FR')})</span>{b.user_name ? <span className="ml-2 text-xs text-[#687175]">— {b.user_name}</span> : null}</div>
                  <div className="flex gap-2">
                    <button onClick={() => loadBrouillon(b)} className="text-xs text-[#F97316] hover:underline">Reprendre</button>
                    <button onClick={() => deleteBrouillon(b.id)} className="text-xs text-red-500 hover:underline">Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === STEP 2: Entreprise === */}
      {step === 2 && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-entreprise">
          <h2 className="text-lg font-semibold text-[#191D1E] mb-4">Informations entreprise</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">SIREN *</label>
              <input type="text" value={formData.siren} maxLength={9} onChange={(e) => setFormData({ ...formData, siren: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" placeholder="123456789" data-testid="siren-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">Nom entreprise *</label>
              <input type="text" value={formData.nom_entreprise} onChange={(e) => setFormData({ ...formData, nom_entreprise: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="nom-entreprise-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">Valeur ajoutee (SIREN)</label>
              <input type="number" value={formData.valeur_ajoutee_siren} onChange={(e) => setFormData({ ...formData, valeur_ajoutee_siren: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="va-input"
                placeholder="Optionnel si exonération NAF" />
              <p className="text-[10px] text-[#687175] mt-1">
                Optionnel — laisser vide si l'éligibilité repose uniquement sur une exonération par code NAF.
              </p>
            </div>
            <div></div>
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">Debut periode fiscale *</label>
              <input type="date" value={formData.debut_periode} onChange={(e) => setFormData({ ...formData, debut_periode: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="debut-periode-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">Fin periode fiscale *</label>
              <input type="date" value={formData.fin_periode} onChange={(e) => setFormData({ ...formData, fin_periode: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="fin-periode-input" />
            </div>
          </div>

          {/* Coche Quotas CO2 — entreprise (mono) ou défaut tous les sites (multi) */}
          <label className="mt-4 flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={!!formData.soumis_quotas_co2}
              onChange={(e) => {
                const val = e.target.checked;
                // En multi : propager à tous les sites existants comme défaut
                if (rapportType === 'multi' && val && (formData.sites || []).length > 0) {
                  const updatedSites = (formData.sites || []).map(s => ({ ...s, soumis_quotas_co2: true }));
                  setFormData({ ...formData, soumis_quotas_co2: val, sites: updatedSites });
                } else {
                  setFormData({ ...formData, soumis_quotas_co2: val });
                }
              }}
              className="w-4 h-4 mt-0.5 text-orange-600 border-orange-400 rounded"
              data-testid="quotas-co2-mono"
            />
            <span className="text-sm text-orange-900">
              <strong>Soumis aux quotas CO2 (SEQE-UE)</strong> — Ouvre le motif tarif réduit
              « QUOTAS_CO2 » pour les PCEs (toujours soumis au ratio TICGN/VAC ≥ 0,5 %).
              {rapportType === 'multi' && (
                <span className="block text-xs mt-1 text-orange-700">
                  En multi-sites : cocher ici applique le défaut à tous les sites. Vous pourrez ensuite
                  décocher individuellement les sites non concernés.
                </span>
              )}
            </span>
          </label>
          <div className="grid grid-cols-2 gap-4 pt-4 mt-4 border-t border-[#BFC6CC]">
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">Agence PDE *</label>
              <select value={formData.agence_id} onChange={(e) => setFormData({ ...formData, agence_id: e.target.value, commercial_id: '' })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="agence-select">
                <option value="">-- Selectionner --</option>
                {agences.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#687175] mb-1">Commercial *</label>
              <select value={formData.commercial_id} disabled={!formData.agence_id} onChange={(e) => setFormData({ ...formData, commercial_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm disabled:bg-[#F5F6F9]" data-testid="commercial-select">
                <option value="">-- Selectionner --</option>
                {commerciauxFiltres.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
              </select>
            </div>
          </div>

          {/* Commentaire de l'analyste (niveau entreprise) */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <label className="block text-xs font-medium text-amber-800 mb-1">Commentaire de l'analyste (global)</label>
            <textarea value={formData.commentaire_global || ''} onChange={(e) => setFormData({ ...formData, commentaire_global: e.target.value })}
              placeholder="Ajoutez ici vos remarques, observations ou points de clarification pour ce dossier..."
              className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[60px] resize-y" rows={2} data-testid="commentaire-global-ticgn" />
            <p className="text-xs text-amber-600 mt-1">Ce commentaire sera inclus dans le rapport final (web + PDF).</p>
          </div>

          {/* === Raccourci VP : visible dès SIREN + Nom remplis === */}
          {formData.siren && formData.nom_entreprise && !vpActivated && (
            <div className="mt-4 border border-dashed border-purple-300 rounded-lg p-3 bg-purple-50/40 flex items-center justify-between gap-3" data-testid="vp-quickjump-block">
              <div className="flex-1 text-sm text-purple-900">
                <strong>💜 Raccourci : renseigner directement la Valeur de Production</strong>
                <div className="text-xs text-purple-700 mt-0.5">
                  Si vous savez déjà que l'éligibilité passera par le ratio VP &gt; 3 %, vous pouvez la
                  pré-remplir maintenant puis revenir au flow normal (PCE, consommations, etc.).
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setVpActivated(true);
                  setVpCameFromQuickJump(true);
                  setStep(VP_STEP);
                }}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm whitespace-nowrap inline-flex items-center gap-1.5"
                data-testid="vp-quickjump-btn"
              >
                Activer la VP →
              </button>
            </div>
          )}
        </div>
      )}

      {/* === STEP 3: PCE / Sites === */}
      {step === 3 && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-pces">
          <h2 className="text-lg font-semibold text-[#191D1E] mb-4">{rapportType === 'mono' ? 'Points de Livraison / Comptage (PDL / PCE)' : 'Sites et points de livraison / comptage'}</h2>

          {rapportType === 'mono' ? (
            <>
              <div className="border border-[#BFC6CC] rounded-lg p-4 mb-4">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-[#687175] mb-1">N° PDL / PCE *</label>
                    <input type="text" value={currentPCE.pce} onChange={(e) => setCurrentPCE({ ...currentPCE, pce: e.target.value })}
                      className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="pce-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#687175] mb-1">Énergie *</label>
                    <select
                      value={currentPCE.energie || 'GAZ_NATUREL'}
                      onChange={(e) => setCurrentPCE({ ...currentPCE, energie: e.target.value })}
                      className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm bg-white"
                      data-testid="pce-energie-select"
                    >
                      {energiesCatalog.length === 0 ? (
                        <option value="GAZ_NATUREL">Gaz naturels combustible</option>
                      ) : (
                        energiesCatalog.map((e) => (
                          <option key={e.code} value={e.code}>{e.libelle} ({e.tarif} {e.unite})</option>
                        ))
                      )}
                    </select>
                  </div>
                  <CodeNAFAutocomplete value={currentPCE.code_naf} onChange={(val) => setCurrentPCE({ ...currentPCE, code_naf: val })} />
                </div>
                <div className="mb-3">
                  <IcpeRubriqueSelector
                    value={currentPCE.icpe_rubrique}
                    onChange={(num) => setCurrentPCE({ ...currentPCE, icpe_rubrique: num })}
                    testId="mono"
                  />
                </div>
                <DoubleUsageBlock
                  value={currentPCE.double_usage}
                  onChange={(du) => setCurrentPCE({ ...currentPCE, double_usage: du })}
                  testId="mono"
                />
                {currentPCE.code_naf && (
                  <div className="mb-3 p-3 bg-white border border-dashed border-[#BFC6CC] rounded-lg">
                    <TranchesEligibilite pce={currentPCE.pce} codeNaf={currentPCE.code_naf} tranches={currentPCE.tranches_eligibilite || []}
                      onChange={(t) => setCurrentPCE({ ...currentPCE, tranches_eligibilite: t })} apiEndpoint="/api/ticgn/naf"
                      icpeMotif={currentPCE.icpe_rubrique ? 'EXO_MINERAL_NON_METALLIQUE' : null} />
                  </div>
                )}
                <button onClick={addPCE} disabled={!currentPCE.pce || !currentPCE.code_naf}
                  className="bg-[#F97316] text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50" data-testid="add-pce-btn">
                  <Plus className="w-4 h-4 inline mr-1" />{editingPCEIndex !== null ? 'Modifier' : 'Ajouter'} PCE
                </button>
              </div>
              {formData.pces.length > 0 && (
                <div className="space-y-2">
                  {formData.pces.map((pce, idx) => (
                    <div key={idx} className={`flex items-center gap-3 p-3 border rounded-lg ${editingPCEIndex === idx ? 'bg-[#FFF7ED] border-[#F97316]' : 'bg-[#F5F6F9] border-[#BFC6CC]'}`}>
                      <div className="flex-1 text-sm">
                        <span className="font-mono font-semibold">{pce.pce}</span>
                        {(() => {
                          const e = energiesCatalog.find(x => x.code === (pce.energie || 'GAZ_NATUREL'));
                          return (
                            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-[#FFF7ED] text-[#C2410C] border border-[#F97316]">
                              {e ? e.libelle : (pce.energie || 'GAZ_NATUREL')}
                            </span>
                          );
                        })()}
                        <span className="text-[#687175] ml-3">{pce.code_naf}</span>
                      </div>
                      <button onClick={() => { setCurrentPCE({ energie: 'GAZ_NATUREL', ...pce }); setEditingPCEIndex(idx); }} className="text-[#F97316] text-xs"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setFormData({ ...formData, pces: formData.pces.filter((_, i) => i !== idx) })} className="text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Multi-sites: Import Excel */}
              <div className="mb-4 flex items-center gap-3">
                <button onClick={downloadSitesTemplate} className="flex items-center gap-1.5 text-sm border border-[#BFC6CC] px-3 py-1.5 rounded-lg text-[#687175] hover:text-[#191D1E]">
                  <Download className="w-4 h-4" /> Template Sites
                </button>
                <label className="flex items-center gap-1.5 text-sm border border-[#FF6B35] px-3 py-1.5 rounded-lg text-[#FF6B35] cursor-pointer hover:bg-[#FFF7ED]">
                  <Upload className="w-4 h-4" /> Import Sites Excel
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportSitesExcel} />
                </label>
              </div>
              {/* Saisie manuelle site */}
              <div className="border border-[#BFC6CC] rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-[#191D1E] mb-3">Nouveau site</h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-[#687175] mb-1">SIRET *</label>
                    <input type="text" value={currentSite.siret} maxLength={14} onChange={(e) => setCurrentSite({ ...currentSite, siret: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="siret-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#687175] mb-1">Nom du site</label>
                    <input type="text" value={currentSite.nom_site} onChange={(e) => setCurrentSite({ ...currentSite, nom_site: e.target.value })}
                      className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="nom-site-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#687175] mb-1">VA du site</label>
                    <input type="number" value={currentSite.valeur_ajoutee} onChange={(e) => setCurrentSite({ ...currentSite, valeur_ajoutee: e.target.value })}
                      className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="va-site-input" />
                  </div>
                </div>
                {/* Commentaire du site */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-amber-800 mb-1">Commentaire du site</label>
                  <textarea value={currentSite.commentaire || ''} onChange={(e) => setCurrentSite({ ...currentSite, commentaire: e.target.value })}
                    placeholder="Remarques ou observations pour ce SIRET..."
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[50px] resize-y" rows={2} data-testid="commentaire-site-ticgn" />
                </div>
                {/* Case taux reduit deja applique */}
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input type="checkbox" checked={currentSite.taux_reduit_deja_applique || false}
                    onChange={(e) => setCurrentSite({ ...currentSite, taux_reduit_deja_applique: e.target.checked })}
                    className="w-4 h-4 text-[#F97316] border-[#BFC6CC] rounded focus:ring-[#F97316]" data-testid="taux-reduit-checkbox" />
                  <span className="text-sm text-[#687175]">Taux reduit deja applique sur ce site (exclure des economies)</span>
                </label>
                {/* Coche Quotas CO2 du site */}
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input type="checkbox" checked={currentSite.soumis_quotas_co2 || false}
                    onChange={(e) => setCurrentSite({ ...currentSite, soumis_quotas_co2: e.target.checked })}
                    className="w-4 h-4 text-orange-600 border-orange-400 rounded" data-testid="quotas-co2-site" />
                  <span className="text-sm text-orange-900">
                    Site soumis aux quotas CO2 (SEQE-UE) — propose le motif QUOTAS_CO2
                  </span>
                </label>
                {/* PCE du site */}
                <div className="border-t border-[#BFC6CC] pt-3">
                  <div className="grid grid-cols-3 gap-3 mb-2">
                    <div>
                      <label className="block text-xs font-medium text-[#687175] mb-1">N° PDL / PCE</label>
                      <input type="text" value={currentPCEMulti.pce} onChange={(e) => setCurrentPCEMulti({ ...currentPCEMulti, pce: e.target.value })}
                        className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="pce-multi-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#687175] mb-1">Énergie *</label>
                      <select
                        value={currentPCEMulti.energie || 'GAZ_NATUREL'}
                        onChange={(e) => setCurrentPCEMulti({ ...currentPCEMulti, energie: e.target.value })}
                        className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm bg-white"
                        data-testid="pce-multi-energie-select"
                      >
                        {energiesCatalog.length === 0 ? (
                          <option value="GAZ_NATUREL">Gaz naturels combustible</option>
                        ) : (
                          energiesCatalog.map((e) => (
                            <option key={e.code} value={e.code}>{e.libelle} ({e.tarif} {e.unite})</option>
                          ))
                        )}
                      </select>
                    </div>
                    <CodeNAFAutocomplete value={currentPCEMulti.code_naf} onChange={(val) => setCurrentPCEMulti({ ...currentPCEMulti, code_naf: val })} />
                  </div>
                  <div className="mb-2">
                    <IcpeRubriqueSelector
                      value={currentPCEMulti.icpe_rubrique}
                      onChange={(num) => setCurrentPCEMulti({ ...currentPCEMulti, icpe_rubrique: num })}
                      testId="multi"
                    />
                  </div>
                  <DoubleUsageBlock
                    value={currentPCEMulti.double_usage}
                    onChange={(du) => setCurrentPCEMulti({ ...currentPCEMulti, double_usage: du })}
                    testId="multi"
                  />
                  {currentPCEMulti.code_naf && (
                    <div className="mb-2 p-3 bg-white border border-dashed border-[#BFC6CC] rounded-lg">
                      <TranchesEligibilite pce={currentPCEMulti.pce} codeNaf={currentPCEMulti.code_naf} tranches={currentPCEMulti.tranches_eligibilite || []}
                        onChange={(t) => setCurrentPCEMulti({ ...currentPCEMulti, tranches_eligibilite: t })} apiEndpoint="/api/ticgn/naf"
                        icpeMotif={currentPCEMulti.icpe_rubrique ? 'EXO_MINERAL_NON_METALLIQUE' : null} />
                    </div>
                  )}
                  <button onClick={addPCEToSite} disabled={!currentPCEMulti.pce || !currentPCEMulti.code_naf}
                    className="text-sm bg-[#F97316] text-white px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid="add-pce-to-site-btn">
                    <Plus className="w-3 h-3 inline mr-1" /> {editingPCEMultiIndex !== null ? 'Modifier' : 'Ajouter'} PCE
                  </button>
                  {editingPCEMultiIndex !== null && (
                    <button onClick={() => { setCurrentPCEMulti({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } }); setEditingPCEMultiIndex(null); }}
                      className="ml-2 text-xs text-[#687175] underline" data-testid="cancel-edit-pce-btn">Annuler</button>
                  )}
                  {currentSite.pces.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <div className="text-xs font-medium text-[#687175]">{currentSite.pces.length} PCE dans ce site</div>
                      {currentSite.pces.map((p, pIdx) => (
                        <div key={pIdx} className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-xs ${editingPCEMultiIndex === pIdx ? 'bg-[#FFF7ED] border-[#F97316]' : 'bg-white border-[#BFC6CC]'}`} data-testid={`site-pce-row-${pIdx}`}>
                          <span className="font-mono font-semibold flex-1">{p.pce}</span>
                          {(() => {
                            const e = energiesCatalog.find(x => x.code === (p.energie || 'GAZ_NATUREL'));
                            return (
                              <span className="px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#C2410C] border border-[#F97316]">
                                {e ? e.libelle : (p.energie || 'GAZ_NATUREL')}
                              </span>
                            );
                          })()}
                          <span className="text-[#687175]">{p.code_naf}</span>
                          {p.tranches_eligibilite?.length > 0 && (
                            <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded">{p.tranches_eligibilite.length} tranche(s)</span>
                          )}
                          <button onClick={() => editPCEInSite(pIdx)} className="text-[#F97316] hover:text-[#FF6B35]" data-testid={`edit-pce-in-site-${pIdx}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removePCEFromSite(pIdx)} className="text-red-500 hover:text-red-700" data-testid={`remove-pce-in-site-${pIdx}`}><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={addSite} disabled={!currentSite.siret || currentSite.pces.length === 0}
                  className="mt-3 w-full bg-[#191D1E] text-white py-2 rounded-lg text-sm disabled:opacity-50" data-testid="add-site-btn">
                  {editingSiteIndex !== null ? 'Enregistrer les modifications du site' : 'Ajouter le site'}
                </button>
                {editingSiteIndex !== null && (
                  <button onClick={() => { setCurrentSite({ siret: '', nom_site: '', valeur_ajoutee: '', taux_reduit_deja_applique: false, commentaire: '', pces: [] }); setEditingSiteIndex(null); setCurrentPCEMulti({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } }); setEditingPCEMultiIndex(null); }}
                    className="mt-2 w-full border border-[#BFC6CC] text-[#687175] py-2 rounded-lg text-sm" data-testid="cancel-edit-site-btn">
                    Annuler la modification
                  </button>
                )}
              </div>
              {/* Sites ajoutes */}
              {formData.sites.map((site, idx) => (
                <div key={idx} className="border border-[#BFC6CC] rounded-lg p-3 mb-2" data-testid={`site-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-sm">{site.nom_site || site.siret}</span>
                      <span className="text-[#687175] text-sm ml-2">({site.pces.length} PCE)</span>
                      {site.taux_reduit_deja_applique && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Taux reduit</span>}
                      {site.valeur_ajoutee && <span className="ml-2 text-xs text-[#687175]">VA: {Number(site.valeur_ajoutee).toLocaleString('fr-FR')} €</span>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setCurrentSite({ ...site, pces: [...(site.pces || [])] }); setEditingSiteIndex(idx); setEditingPCEMultiIndex(null); setCurrentPCEMulti({ pce: '', energie: 'GAZ_NATUREL', code_naf: '', icpe_rubrique: null, tranches_eligibilite: [], double_usage: { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' } }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-[#F97316] text-xs" data-testid={`edit-site-${idx}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setFormData({ ...formData, sites: formData.sites.filter((_, i) => i !== idx) })} className="text-red-500" data-testid={`remove-site-${idx}`}><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {site.pces && site.pces.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-[#F5F6F9] space-y-1">
                      {site.pces.map((p, pi) => (
                        <div key={pi} className="text-xs text-[#687175] flex items-center gap-2">
                          <span className="font-mono font-semibold text-[#191D1E]">{p.pce}</span>
                          <span>{p.code_naf}</span>
                          {p.tranches_eligibilite?.length > 0 && p.tranches_eligibilite.map((t, ti) => (
                            <span key={ti} className="px-1 py-0.5 bg-orange-50 text-orange-700 rounded">{t.part}% {t.motif}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* === STEP 4: Consommations === */}
      {step === 4 && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-consommations">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#191D1E]">Consommations mensuelles (kWh)</h2>
            <div className="flex items-center gap-2">
              <button onClick={downloadConsoTemplate} className="flex items-center gap-1.5 text-sm text-[#687175] hover:text-[#191D1E] border border-[#BFC6CC] px-3 py-1.5 rounded-lg" data-testid="download-template-btn">
                <Download className="w-4 h-4" /> Template
              </button>
              <label className="flex items-center gap-1.5 text-sm text-[#FF6B35] border border-[#FF6B35] px-3 py-1.5 rounded-lg cursor-pointer" data-testid="import-excel-btn">
                <Upload className="w-4 h-4" /> Import Excel
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportConsommationsExcel} />
              </label>
            </div>
          </div>
          {moisPeriode.length === 0 ? (
            <p className="text-[#687175]">Definissez la periode a l'etape 2.</p>
          ) : (
            allPCEs.map((pce) => (
              <div key={pce.pce} className="mb-6 border border-[#BFC6CC] rounded-lg overflow-hidden">
                <div className="bg-[#F5F6F9] px-4 py-2.5 border-b border-[#BFC6CC] flex items-center justify-between">
                  <div>
                    <span className="font-mono font-semibold text-[#F97316]">{pce.pce}</span>
                    <span className="text-sm text-[#687175] ml-3">{pce.code_naf}</span>
                    {pce.nom_site && <span className="text-sm text-[#687175] ml-2">({pce.nom_site})</span>}
                  </div>
                  <span className="text-xs text-[#687175] flex items-center gap-1"><ClipboardPaste className="w-3 h-3" /> Collez dans le 1er champ</span>
                </div>
                <div className="p-4 grid grid-cols-4 gap-2">
                  {moisPeriode.map(({ key, label }, mIdx) => (
                    <div key={key}>
                      <label className="block text-[10px] text-[#687175] mb-0.5">{label}</label>
                      <input type="number" placeholder="kWh"
                        value={consommationsParPCE[pce.pce]?.[key] || ''}
                        onPaste={mIdx === 0 ? (e) => handlePasteConsommations(pce.pce, e) : undefined}
                        onChange={(e) => setConsommationsParPCE(prev => ({ ...prev, [pce.pce]: { ...prev[pce.pce], [key]: parseFloat(e.target.value) || 0 } }))}
                        className={`w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm font-mono ${mIdx === 0 ? 'ring-1 ring-orange-200' : ''}`}
                        data-testid={`conso-${pce.pce}-${key}`} />
                    </div>
                  ))}
                </div>
                <div className="px-4 pb-3">
                  <span className="text-xs text-[#687175]">Total : <span className="font-mono font-semibold text-[#191D1E]">
                    {(Object.values(consommationsParPCE[pce.pce] || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0) / 1000).toFixed(1)} MWh
                  </span></span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* === STEP 5: Recapitulatif === */}
      {step === 5 && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-recap">
          <h2 className="text-lg font-semibold text-[#191D1E] mb-4">Recapitulatif</h2>
          <div className="bg-[#F5F6F9] rounded-lg p-4 mb-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><span className="text-[#687175]">Entreprise :</span><div className="font-semibold">{formData.nom_entreprise}</div></div>
              <div><span className="text-[#687175]">SIREN :</span><div className="font-mono font-semibold">{formData.siren}</div></div>
              <div><span className="text-[#687175]">Periode :</span><div>{formData.debut_periode ? new Date(formData.debut_periode).toLocaleDateString('fr-FR') : ''} - {formData.fin_periode ? new Date(formData.fin_periode).toLocaleDateString('fr-FR') : ''}</div></div>
            </div>
            <div className="mt-2 flex gap-3">
              <button onClick={() => setStep(2)} className="text-xs text-[#F97316] hover:underline">Modifier infos</button>
              <button onClick={() => setStep(3)} className="text-xs text-[#F97316] hover:underline">Modifier PCE</button>
              <button onClick={() => setStep(4)} className="text-xs text-[#F97316] hover:underline">Modifier consommations</button>
            </div>
          </div>
          {/* PCE recap */}
          <div className="space-y-2 mb-4">
            {allPCEs.map((pce, idx) => {
              const pceConso = Object.values(consommationsParPCE[pce.pce] || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0);
              return (
                <div key={idx} className="p-3 border border-[#BFC6CC] rounded-lg text-sm flex items-center gap-4">
                  <span className="font-mono font-semibold">{pce.pce}</span>
                  <span className="text-[#687175]">{pce.code_naf}</span>
                  <span className="text-[#687175]">{(pceConso / 1000).toFixed(1)} MWh</span>
                  {pce.tranches_eligibilite?.length > 0 && pce.tranches_eligibilite.map((t, ti) => (
                    <span key={ti} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">{t.part}% {t.motif}</span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === STEP 6: Validation avant generation === */}
      {step === 6 && (
        <StepValidationTICGN ctx={{
          formData, consommationsParPCE, allPCEs, nafEligMap, rapportType,
          loading, searchParams, handleGenerate,
          vpActivated,
          onActivateVP: () => {
            setVpActivated(true);
            setVpCameFromQuickJump(false);
            setStep(VP_STEP);
          },
        }} />
      )}

      {/* === STEP 7: Valeur de Production (apparaît uniquement si vpActivated) === */}
      {step === VP_STEP && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-vp">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-[#191D1E] flex items-center gap-2">
                💜 Valeur de Production — Tarif réduit énergo-intensive
              </h2>
              <p className="text-sm text-[#687175] mt-1">
                Activez le tarif réduit <strong>RATIO_VALEUR_PRODUCTION</strong> si
                <strong> Σ (Consommation × Tarif fiscal) / (CA + Export − Import) &gt; 3 %</strong> (art. 265 nonies CdD).
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!confirm('Désactiver la Valeur de Production ? Les données saisies seront conservées mais non envoyées au calcul.')) return;
                setVpActivated(false);
                setVpCameFromQuickJump(false);
                setStep(vpCameFromQuickJump ? 2 : 6);
              }}
              className="text-xs text-red-600 hover:text-red-700 hover:underline whitespace-nowrap"
              data-testid="vp-deactivate-btn"
            >
              Désactiver la VP
            </button>
          </div>

          {/* === Bloc VP SIREN === */}
          <div className="border border-dashed border-purple-300 rounded-lg p-4 bg-purple-50/40" data-testid="vp-block">
            <h3 className="text-sm font-semibold text-purple-900 mb-2">Niveau SIREN</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-purple-900 mb-1">Chiffre d'affaires (€)</label>
                <input type="number" min="0" value={formData.valeur_production.ca}
                  onChange={(e) => setFormData({ ...formData, valeur_production: { ...formData.valeur_production, ca: e.target.value } })}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm bg-white"
                  data-testid="vp-ca" />
              </div>
              <div>
                <label className="block text-xs text-purple-900 mb-1">Exportations (€)</label>
                <input type="number" min="0" value={formData.valeur_production.export}
                  onChange={(e) => setFormData({ ...formData, valeur_production: { ...formData.valeur_production, export: e.target.value } })}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm bg-white"
                  data-testid="vp-export" />
              </div>
              <div>
                <label className="block text-xs text-purple-900 mb-1">Importations (€)</label>
                <input type="number" min="0" value={formData.valeur_production.import}
                  onChange={(e) => setFormData({ ...formData, valeur_production: { ...formData.valeur_production, import: e.target.value } })}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm bg-white"
                  data-testid="vp-import" />
              </div>
            </div>
            <p className="text-xs text-purple-900 font-medium mb-2">
              Détail des consommations énergétiques sur la période fiscale
              <span className="ml-1 text-[10px] text-purple-700 italic">(le coût fiscal est calculé automatiquement : Conso × Tarif fiscal officiel)</span>
            </p>
            <LignesEnergiesEditor
              lignes={formData.lignes_energies || []}
              onChange={(next) => setFormData({ ...formData, lignes_energies: next })}
              testIdPrefix="vp-ligne"
            />
            {(() => {
              const ca = parseFloat(formData.valeur_production.ca) || 0;
              const exp = parseFloat(formData.valeur_production.export) || 0;
              const imp = parseFloat(formData.valeur_production.import) || 0;
              const vp = ca + exp - imp;
              const total = (formData.lignes_energies || []).reduce(
                (s, l) => s + (parseFloat(l.consommation_mwh) || 0) * (parseFloat(l.tarif_fiscal) || 0), 0
              );
              if (vp <= 0) return null;
              const ratio = total / vp;
              const eligible = ratio > 0.03;
              return (
                <div className={`mt-3 p-3 rounded-lg text-sm ${eligible ? 'bg-emerald-50 border border-emerald-300 text-emerald-800' : 'bg-amber-50 border border-amber-300 text-amber-800'}`} data-testid="vp-preview">
                  <div className="font-medium">
                    Ratio VP estimé : <strong>{(ratio * 100).toFixed(2).replace('.', ',')} %</strong> ({eligible ? 'Éligible ✓' : `≤ 3 %, non éligible par cette voie`})
                  </div>
                  <div className="text-xs mt-1">
                    Σ (Conso × Tarif) = {total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € — VP = {vp.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                  </div>
                </div>
              );
            })()}
          </div>

          {/* === Variante VP par SIRET (multi-sites) === */}
          {rapportType === 'multi' && (formData.sites || []).length > 0 && (
            <details className="mt-4 border border-purple-200 rounded-lg p-3 bg-white" open>
              <summary className="cursor-pointer text-sm font-semibold text-purple-900 select-none">
                ↪️ Variante : saisie VP par SIRET (fallback si SIREN &lt; 3 %)
              </summary>
              <p className="text-xs text-purple-800 mt-2 mb-3">
                Si le ratio SIREN ne passe pas, vous pouvez le calculer site par site. Seuls
                les PCEs des SIRETs &gt; 3 % deviennent éligibles à <strong>RATIO_VALEUR_PRODUCTION</strong>.
              </p>
              <div className="space-y-3">
                {(formData.sites || []).map((site, sIdx) => {
                  const key = site.siret || `site-${sIdx}`;
                  const vp = formData.vp_par_siret?.[key] || {};
                  const ca = parseFloat(vp.ca) || 0;
                  const exp = parseFloat(vp.export) || 0;
                  const imp = parseFloat(vp.import) || 0;
                  const vpVal = ca + exp - imp;
                  const totalF = (vp.lignes_energies || []).reduce(
                    (s, l) => s + (parseFloat(l.consommation_mwh) || 0) * (parseFloat(l.tarif_fiscal) || 0), 0
                  );
                  const ratio = vpVal > 0 ? totalF / vpVal : 0;
                  const eligible = ratio > 0.03;
                  const updateSiret = (patch) => setFormData({
                    ...formData,
                    vp_par_siret: { ...(formData.vp_par_siret || {}), [key]: { ...vp, ...patch } }
                  });
                  return (
                    <div key={key} className="border border-purple-200 rounded p-3 bg-purple-50/30" data-testid={`vp-siret-row-${key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-purple-900">
                          <span className="font-mono">{site.siret || '—'}</span> — {site.nom_site || 'Site'}
                        </div>
                        {totalF > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded ${eligible ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            Ratio {(ratio * 100).toFixed(2).replace('.', ',')} % {eligible ? '✓' : '✗'}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                        <input type="number" min="0" placeholder="CA (€)" value={vp.ca || ''}
                          onChange={(e) => updateSiret({ ca: e.target.value })}
                          className="px-2 py-1 border border-purple-300 rounded bg-white"
                          data-testid={`vp-siret-ca-${key}`} />
                        <input type="number" min="0" placeholder="Export (€)" value={vp.export || ''}
                          onChange={(e) => updateSiret({ export: e.target.value })}
                          className="px-2 py-1 border border-purple-300 rounded bg-white"
                          data-testid={`vp-siret-export-${key}`} />
                        <input type="number" min="0" placeholder="Import (€)" value={vp.import || ''}
                          onChange={(e) => updateSiret({ import: e.target.value })}
                          className="px-2 py-1 border border-purple-300 rounded bg-white"
                          data-testid={`vp-siret-import-${key}`} />
                      </div>
                      <LignesEnergiesEditor
                        lignes={vp.lignes_energies || []}
                        onChange={(next) => updateSiret({ lignes_energies: next })}
                        compact
                        testIdPrefix={`vp-siret-ligne-${key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </details>
          )}

          {/* === Actions === */}
          {vpCameFromQuickJump && allPCEs.length === 0 ? (
            // Mode raccourci : retour au flow normal
            <div className="mt-6 flex items-center justify-between gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-900">
                📌 VP pré-remplie. Continuez avec les PCE / consommations pour générer le rapport.
              </span>
              <button
                type="button"
                onClick={() => { setVpCameFromQuickJump(false); setStep(3); }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm whitespace-nowrap inline-flex items-center gap-1.5"
                data-testid="vp-continue-flow-btn"
              >
                Continuer vers les PCE / Sites <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (() => {
            const vpFilled = (parseFloat(formData.valeur_production?.ca) || 0) > 0
              && (formData.lignes_energies || []).some(l => (parseFloat(l.consommation_mwh) || 0) > 0);
            const vpSiretFilled = rapportType === 'multi' && Object.values(formData.vp_par_siret || {}).some(v =>
              (parseFloat(v.ca) || 0) > 0 && (v.lignes_energies || []).some(l => (parseFloat(l.consommation_mwh) || 0) > 0)
            );
            const canGenerate = vpFilled || vpSiretFilled;
            return (
              <div className="mt-6">
                {!canGenerate && (
                  <div className="mb-2 p-2 text-xs rounded bg-amber-50 border border-amber-300 text-amber-800 text-center" data-testid="vp-empty-warning">
                    ⚠️ Renseignez au moins le CA et une ligne énergie (SIREN ou SIRET) avant de générer le rapport.
                  </div>
                )}
                <button onClick={handleGenerate} disabled={loading || !canGenerate}
                  className="w-full bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white py-4 rounded-xl font-semibold text-lg hover:from-[#EA580C] hover:to-[#C2410C] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="generate-btn-vp">
                  {loading
                    ? <><span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> Calcul en cours...</>
                    : <><FileText className="w-5 h-5" /> {searchParams.get('edit') ? 'Mettre à jour le rapport (avec VP)' : 'Générer le Rapport avec VP'}</>}
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* === STEP 8: Resultats === */}
      {step === RESULT_STEP && resultats && (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-resultats">
          <div className="text-center mb-6">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${resultats.eligibilite_globale === 'ELIGIBLE' ? 'bg-emerald-100' : 'bg-red-100'}`}>
              {resultats.eligibilite_globale === 'ELIGIBLE' ? <Check className="w-8 h-8 text-emerald-600" /> : <X className="w-8 h-8 text-red-600" />}
            </div>
            <h2 className="text-2xl font-bold mt-3">{resultats.eligibilite_globale}</h2>
            <p className="text-[#687175]">{resultats.nom_entreprise} - SIREN {resultats.siren}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-[#F5F6F9] rounded-lg">
              <div className="text-sm text-[#687175]">Ratio CERFA</div>
              <div className="text-xl font-bold">{(resultats.ratio_global * 100).toFixed(3)}%</div>
            </div>
            <div className="text-center p-4 bg-[#F5F6F9] rounded-lg">
              <div className="text-sm text-[#687175]">TICGN CERFA officiel</div>
              <div className="text-xl font-bold text-[#FF6B35]">{formatEur(resultats.total_ticgn_cerfa_officiel)}</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <div className="text-sm text-emerald-600">Economies attestation</div>
              <div className="text-xl font-bold text-emerald-600">{formatEur(resultats.economies_attestation_total)}</div>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to={`/rapports/ticgn/${resultats.id}`} className="flex-1 text-center bg-[#F97316] text-white py-3 rounded-lg font-medium hover:bg-[#EA580C]">
              Consulter le rapport
            </Link>
            <button onClick={() => { setStep(1); setResultats(null); setVpActivated(false); setVpCameFromQuickJump(false); setFormData({ siren: '', nom_entreprise: '', valeur_ajoutee_siren: '', commentaire_global: '', debut_periode: '', fin_periode: '', agence_id: '', commercial_id: '', pces: [], sites: [], soumis_quotas_co2: false, valeur_production: { ca: '', export: '', import: '' }, factures_htva: { elec: '', gaz: '', autres_energies: '' }, lignes_energies: [], vp_par_siret: {} }); setConsommationsParPCE({}); }}
              className="flex-1 text-center border border-[#BFC6CC] text-[#687175] py-3 rounded-lg font-medium hover:bg-[#F5F6F9]">
              Nouveau rapport
            </button>
          </div>
        </div>
      )}

      {/* Navigation footer — masquée sur Résultats et sur la step VP (boutons internes) */}
      {step <= 6 && step !== RESULT_STEP && (
        <div className="flex justify-between items-center mt-6">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
            className="flex items-center gap-2 px-6 py-2.5 border border-[#BFC6CC] rounded-lg text-[#687175] disabled:opacity-30">
            <ArrowLeft className="w-4 h-4" /> Precedent
          </button>
          {step >= 2 && (
            <button
              type="button"
              onClick={handleManualSaveBrouillon}
              className="flex items-center gap-2 px-6 py-2.5 border border-[#F9C80E] text-[#F9C80E] font-medium rounded-lg hover:bg-[#FFF9E6]"
              data-testid="save-draft-btn-ticgn"
              title="Enregistrer la progression actuelle pour la reprendre plus tard"
            >
              <Save className="w-4 h-4" /> Enregistrer Brouillon
            </button>
          )}
          {step < 6 ? (
            <button onClick={() => { if (step === 5) saveBrouillon(); setStep(step + 1); }} disabled={!canNext()}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#F97316] text-white rounded-lg disabled:opacity-30" data-testid="next-step-btn">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          ) : step === 6 && vpActivated ? (
            <button onClick={() => setStep(VP_STEP)}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg" data-testid="goto-vp-btn">
              Aller à la Valeur de Production <ArrowRight className="w-4 h-4" />
            </button>
          ) : <div />}
        </div>
      )}

      {/* Modal Bascule SIRET */}
      <ModalBasculeSIRET
        isOpen={showModalBasculeSIRET}
        onClose={() => { setShowModalBasculeSIRET(false); setResultatsParSiret(null); }}
        onConfirm={handleConfirmBasculeSIRET}
        eligibiliteData={eligibiliteData}
        onCalculerParSiret={handleCalculerParSiret}
        resultatsParSiret={resultatsParSiret}
        isCalculating={isCalculatingSiret}
      />
    </div>
  );
};

export default CreateRapportTICGN;
